import React, { ComponentProps, useEffect, useState, useRef } from "react"

import type { GalleryProps } from "./Gallery"
import InfoSection from "./InfoSection"
import { InfoSectionStickyFooter } from "./StickyFooter"
import Variants from "./Variants"
import { REASON_NOT_AVAILABLE_MAPPING } from "../../../../cart/constants"
import { useGetContextProduct } from "../../../../cart/hooks"

import AddToCartButton from "@components/elements/V2/Buttons/AddToCartButton"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import AccordionArrowIcon from "@components/elements/V2/Icons/AccordionArrowIcon"
import QuantityInput from "@components/elements/V2/Inputs/QuantityInput"
import { NumberDisplayValueProps } from "@components/elements/V2/NumberDisplay"
import RecommendedProduct from "@components/elements/V2/RecommendedProduct"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { PurchasableItem } from "@hooks/V2/storyblok"

type Props = {
  superscript?: string
  consolidatedProduct: YocoCom.ConsolidatedCartItem
  productName: string
  productDescription?: string
  price: NumberDisplayValueProps
  waitlistLink?: string
  productUrl: string
  contentSections: Storyblok.InfoSection[]
  deliveryTitle?: string
  deliveryDescription?: string
  purchasableItem: PurchasableItem
  recommendedProducts?: (Storyblok.Product | Storyblok.Bundle)[]
  showRecommendedProducts: boolean
  recommendedLabel: string
  variantDescription?: string
  selectedVariantOption: number
  variantOptions?: ComponentProps<typeof Variants>["options"]
  variantOptionsOnChange?: ComponentProps<typeof Variants>["onChange"]
  showPayflexSection?: boolean
  mobileGridOrder?: number
  badge?: Storyblok.Badge
} & GalleryProps

const ProductInfoSection = ({
  superscript,
  consolidatedProduct,
  productName,
  productDescription,
  price,
  waitlistLink,
  productUrl,
  contentSections,
  galleryItems,
  deliveryTitle,
  deliveryDescription,
  purchasableItem,
  showRecommendedProducts,
  recommendedProducts,
  recommendedLabel,
  variantDescription,
  selectedVariantOption,
  variantOptions,
  variantOptionsOnChange,
  showPayflexSection,
  mobileGridOrder,
  badge,
}: Props) => {
  const contextProduct = useGetContextProduct(purchasableItem)
  const initialQuantity = consolidatedProduct?.quantity
  const [quantity, setQuantity] = useState(initialQuantity ?? 1)

  useEffect(() => {
    setQuantity(contextProduct?.quantity ?? 1)
  }, [contextProduct?.quantity])

  const scrollRef = useRef<HTMLDivElement>(null)
  const handleClick = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }, 100)
  }
  superscript = consolidatedProduct.reasonNotAvailable
    ? REASON_NOT_AVAILABLE_MAPPING[consolidatedProduct.reasonNotAvailable]
    : superscript
        ?.split(",")
        .map((s) => s.trim())
        .join(" · ")

  const displayRecommendedProducts =
    showRecommendedProducts &&
    recommendedProducts &&
    recommendedProducts.length > 0

  const selectionContent = !!(
    variantOptions &&
    variantOptions.length > 1 &&
    variantOptionsOnChange
  ) && (
    <Variants
      description={variantDescription}
      selectedOption={selectedVariantOption}
      options={variantOptions}
      onChange={variantOptionsOnChange}
      className="mb-24"
    />
  )

  const accessoriesTriggerContent = displayRecommendedProducts ? (
    <div className="mb-40 inline-flex w-full items-center gap-x-1 border-y border-charcoal/20 py-20">
      <Typography
        leadingNone
        font="grotesk"
        size="body-lg"
        weight="medium"
        className="inline"
        color={Color.Black}
        text="Looking for accessories?"
      />
      <TextButton
        style="icon"
        text="Click here"
        color={Color.Blue}
        className="inline"
        textSize="body-lg"
        linkUrl="#recommended-products"
        icon={<AccordionArrowIcon open={false} color={Color.Blue} />}
      />
    </div>
  ) : null

  const footerContent = (
    <InfoSectionStickyFooter>
      <AddToCartButton
        style="solid"
        purchasableItem={purchasableItem}
        quantity={quantity}
        changeCartQuantity={quantity !== contextProduct?.quantity}
        reasonNotAvailable={consolidatedProduct.reasonNotAvailable}
        className="!w-full"
        price={
          typeof price?.value === "string"
            ? parseFloat(price?.value)
            : typeof price?.value === "number"
              ? price?.value
              : undefined
        }
        waitlistLink={waitlistLink}
        onClick={handleClick}
      />
    </InfoSectionStickyFooter>
  )
  {
    displayRecommendedProducts ? (
      <div ref={scrollRef} className="scroll-mt-24" id="recommended-products">
        <Typography
          text={recommendedLabel}
          size="lead-sm"
          font="grotesk"
          weight="medium"
          color={Color.Black}
          className="py-24"
        />
        <div className="flex flex-col gap-20 sm:grid-cols-6">
          {recommendedProducts?.map((product, index) => (
            <RecommendedProduct key={index} product={product} openCartPreview />
          ))}
        </div>
      </div>
    ) : null
  }

  const quantityInput = !consolidatedProduct.reasonNotAvailable && (
    <QuantityInput
      value={quantity}
      maxValue={consolidatedProduct.maxQuantity}
      onQuantityUpdate={(quantity) => setQuantity(quantity)}
      disabled={consolidatedProduct.maxQuantity === 0}
    />
  )

  return (
    <InfoSection
      mobileGridOrder={mobileGridOrder}
      galleryItems={galleryItems}
      name={productName}
      description={productDescription}
      productUrl={productUrl}
      superscript={superscript}
      deliveryTitle={deliveryTitle}
      deliveryDescription={deliveryDescription}
      contentSections={contentSections}
      price={consolidatedProduct.price}
      originalPrice={consolidatedProduct.originalPrice}
      priceSubscriptText={consolidatedProduct.priceSubScript}
      promotionStartDate={consolidatedProduct.promotionStartDate}
      promotionEndDate={consolidatedProduct.promotionEndDate}
      selectionContent={selectionContent}
      sideSelectionContent={quantityInput}
      middleContent={accessoriesTriggerContent}
      footerContent={footerContent}
      showPayflexSection={showPayflexSection}
      originalPriceLabel="WAS"
      overrideShowDiscount={false}
      badge={badge}
    />
  )
}

export default ProductInfoSection
